/**
 * Pure helpers for print macros.
 *
 * A macro is a list of pages an admin printed, recorded against a baseline: the
 * production days they were printing for. Every one of those dates found in a
 * recorded route is replaced by a token, so the macro can be replayed for another
 * set of production days and every page shows that week's data. The production or
 * shipping group each page shows (local, ship, CC) is kept exactly as recorded: a
 * macro can mix pages for ship and local, and replaying only changes the dates.
 *
 * Tokens (all inside the url path or query):
 *   {{dates}}        a query param holding every production date (?dates=a&dates=b)
 *   {{date:N}}       the Nth (sorted) production date - used where a page takes a
 *                    single day, e.g. /meals/{{date:0}}_local or ?date={{date:2}}
 *
 * Macros recorded before 2026-09-14 hold a {{production}} token too; fillProductionToken
 * writes their recorded production back into them when they are loaded.
 */
import {getProductionDays} from '@/store/utils';
import {printPageCssEnabled} from '@/utils/print-page';

const DATE_PATTERN = /\d{4}-\d{2}-\d{2}/g;
// a path segment naming a day and the group it is for: /meals/2026-09-14_local
const DATE_GROUP_PATTERN = /(\d{4}-\d{2}-\d{2})_([A-Za-z]+)/;
const TOKEN_OR_DATE_GROUP_PATTERN = /(?:\d{4}-\d{2}-\d{2}|{{date:\d+}})_([A-Za-z]+)(?=$|[/?&])/;
const TOKEN_PATTERN = /{{(dates|date:(\d+))}}/g;
const DATE_TOKEN = (index) => `{{date:${index}}}`;
const DATES_TOKEN = '{{dates}}';
export const PRODUCTION_TOKEN = '{{production}}';
// the query params pages use for the group they show, most specific first
const GROUP_PARAMS = ['shippinggroup', 'production'];

export function sortedDates(dates) {
  return [...(dates || [])].filter(Boolean).sort();
}

export function slugify(text) {
  return `${text || ''}`
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'page';
}

/** 'ComponentPrep2' -> 'Component Prep 2' - same idea as App.vue's toWords */
export function routeNameToWords(name) {
  return `${name || ''}`
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])(\d)/g, '$1 $2')
    .trim();
}

function tokeniseDateText(text, baselineDates, warnings) {
  return `${text}`.replace(DATE_PATTERN, (date) => {
    const index = baselineDates.indexOf(date);
    if (index < 0) {
      warnings.push(`date ${date} is not one of the recording production's dates and will replay unchanged`);
      return date;
    }
    return DATE_TOKEN(index);
  });
}

/**
 * Turn the route the admin is looking at into a replayable step: the baseline dates
 * become tokens, everything else (the production or shipping group included) stays.
 *
 * @param route vue-router route ({name, path, query})
 * @param baseline {dates} the production days the admin recorded against
 * @returns {{name, label, path, query, warnings}}
 */
export function captureStep(route, baseline) {
  if (!isCompleteTarget(baseline)) {
    const query = {};
    Object.entries(route.query || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        query[key] = value;
      }
    });
    return {
      name: route.name || route.path,
      label: routeNameToWords(route.name) || route.path,
      path: route.path,
      query,
      warnings: ['no production days known yet: dates in this page will be matched when the recording is finished'],
    };
  }
  const warnings = [];
  const baselineDates = sortedDates(baseline && baseline.dates);

  const path = tokeniseDateText(route.path, baselineDates, warnings);

  const query = {};
  Object.entries(route.query || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    if (key === 'dates') {
      const routeDates = sortedDates([].concat(value));
      if (baselineDates.length > 0 && routeDates.join() === baselineDates.join()) {
        query[key] = DATES_TOKEN;
      } else {
        query[key] = [].concat(value).map(d => tokeniseDateText(d, baselineDates, warnings));
        if (baselineDates.length > 0) {
          warnings.push(`?dates in the url (${routeDates.join(', ')}) differ from the recording production's dates`);
        }
      }
    } else if (Array.isArray(value)) {
      query[key] = value.map(v => tokeniseDateText(v, baselineDates, warnings));
    } else {
      query[key] = tokeniseDateText(value, baselineDates, warnings);
    }
  });

  return {
    name: route.name || path,
    label: routeNameToWords(route.name) || path,
    path,
    query,
    warnings: [...new Set(warnings)],
  };
}

function fillTokens(text, target) {
  const dates = sortedDates(target.dates);
  return `${text}`.replace(TOKEN_PATTERN, (match, token, dateIndex) => {
    if (token === 'dates') {
      return dates.join(',');
    }
    // a step recorded against a 3 day production replayed on a shorter one: use the last day
    return dates[Math.min(Number(dateIndex), dates.length - 1)] || '';
  });
}

/**
 * Build the route to visit for a step on other production days.
 * Returns a vue-router location ({path, query}).
 */
export function resolveStep(step, target) {
  const query = {};
  Object.entries(step.query || {}).forEach(([key, value]) => {
    if (value === DATES_TOKEN) {
      query[key] = sortedDates(target.dates);
    } else if (Array.isArray(value)) {
      query[key] = value.map(v => fillTokens(v, target));
    } else {
      query[key] = fillTokens(value, target);
    }
  });
  return {path: fillTokens(step.path, target), query};
}

// characters no file system accepts in a name
const ILLEGAL_FILENAME = /[\\/:*?"<>|]+/g;

export function cleanFilename(text) {
  return `${text || ''}`.replace(ILLEGAL_FILENAME, '-').replace(/\s+/g, ' ').trim().replace(/\.pdf$/i, '').trim();
}

/**
 * The production or shipping group a page shows, read from its own url:
 * ?shippinggroup= or ?production= (whichever is set), or a /2026-09-14_local path
 * segment. '' when the page has none.
 */
export function stepGroup(step) {
  const query = (step && step.query) || {};
  const param = GROUP_PARAMS.find(key => typeof query[key] === 'string' && query[key] && query[key] !== PRODUCTION_TOKEN);
  if (param) {
    return query[param];
  }
  const match = TOKEN_OR_DATE_GROUP_PATTERN.exec((step && step.path) || '');
  return match ? match[1] : '';
}

/** the groups a macro's pages show, in the order they first appear: ['ship', 'local'] */
export function macroGroups(macro) {
  return [...new Set(((macro && macro.steps) || []).map(stepGroup).filter(Boolean))];
}

/** e.g. '2026-09-14-local-' for a target and group; '' while the dates are not known */
export function filenamePrefix(target, group = '') {
  const [firstDate] = sortedDates(target && target.dates);
  if (!firstDate) {
    return '';
  }
  return `${[firstDate, group].filter(Boolean).join('-')}-`;
}

/**
 * Every file a replay saves is named <first production date>-<group>-<name>.pdf: the
 * group is the production or shipping group the page shows, the name what the admin
 * typed when capturing the page (or the page's name). e.g. 2026-09-14-local-component-prep.pdf
 */
export function stepFilename(step, target) {
  const name = cleanFilename(step.filename) || slugify(step.label || step.name);
  return `${filenamePrefix(target, stepGroup(step))}${name}.pdf`;
}

/** the text file listing every page a replay saved and the url it used */
export function linksFilename(target, macroName) {
  return `${filenamePrefix(target, slugify(macroName))}links.txt`;
}

/**
 * The production days a page is showing, read from its url: ?dates= on selector pages,
 * or a /2026-09-14_local path segment (expanded to that day's production days).
 * Returns null when the url does not say.
 */
export function inferBaseline(route) {
  const query = (route && route.query) || {};
  const dates = sortedDates([].concat(query.dates || []));
  if (dates.length > 0) {
    return {dates};
  }
  const match = DATE_GROUP_PATTERN.exec(route && route.path);
  if (match) {
    return {dates: getProductionDays(match[1])};
  }
  return null;
}

export function describeTarget(target) {
  const dates = sortedDates(target && target.dates);
  if (dates.length === 0) {
    return '';
  }
  return dates.length === 1 ? dates[0] : `${dates[0]} to ${dates[dates.length - 1]}`;
}

export function isCompleteTarget(target) {
  return !!(target && sortedDates(target.dates).length > 0);
}

/**
 * Macros recorded before the group stayed literal hold {{production}} where their
 * recording production was: write it back so they replay as they were recorded.
 * Steps without the token come back unchanged.
 */
export function fillProductionToken(steps, production) {
  const fill = text => typeof text === 'string' ? text.split(PRODUCTION_TOKEN).join(production) : text;
  return (steps || []).map((step) => {
    if (!production || !stepUrl(step).includes(PRODUCTION_TOKEN)) {
      return step;
    }
    const query = {};
    Object.entries(step.query || {}).forEach(([key, value]) => {
      query[key] = Array.isArray(value) ? value.map(fill) : fill(value);
    });
    return {...step, path: fill(step.path), query};
  });
}

export function newMacroId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

// keep tokens and the characters dates and lists use readable in an editable url
function encodeQueryPart(text) {
  return encodeURIComponent(text)
    .replace(/%7B/gi, '{').replace(/%7D/gi, '}')
    .replace(/%3A/gi, ':').replace(/%2C/gi, ',');
}

/**
 * What the server needs to render a macro for some production days: the pages as the
 * part of the kitchen url after # and the file each becomes, and where the pdfs go
 * (download, or the macro's slack channel). hrefFor turns a resolved location into
 * that url part (the router's href without its leading #). pageCss says whether the
 * pages' own @page sizes apply (this browser's print-page toggle), so the server prints
 * the way this browser would.
 */
export function serverRenderRequest(macro, target, hrefFor, {pageCss = printPageCssEnabled()} = {}) {
  return {
    macroName: macro.name,
    orientation: macro.orientation || 'portrait',
    pageCss,
    target: {dates: sortedDates(target.dates)},
    // a slack delivery: the server posts the pdfs to the channel instead of keeping them for download
    slack: macro.delivery === 'slack' && macro.slackChannel && macro.slackChannel.id
      ? {name: macro.slackChannel.name, id: macro.slackChannel.id} : null,
    steps: (macro.steps || []).map(step => ({
      label: step.label || step.name,
      href: hrefFor(resolveStep(step, target)),
      filename: stepFilename(step, target),
    })),
  };
}

const STATUS_NOTE = {
  done: '',
  'done-unsettled': ' (page was still loading when printed)',
  failed: ' (FAILED)',
  cancelled: ' (cancelled before it was printed)',
};

/**
 * The links list saved with a render: every pdf and the url it was printed from.
 * @param results [{filename, href, status, error}]
 */
export function replayLinksText({macroName, target, results, generatedAt = new Date()}) {
  const lines = [
    `Print macro: ${macroName}`,
    `Production days: ${describeTarget(target)}`,
    `Rendered: ${generatedAt.toLocaleString()}`,
    '',
  ];
  results.forEach((result) => {
    lines.push(`${result.filename}${STATUS_NOTE[result.status] || ''}`);
    lines.push(`${result.href}${result.error ? `\n  error: ${result.error}` : ''}`);
    lines.push('');
  });
  return lines.join('\n');
}

/**
 * The pdf button in the app bar: the page being looked at as a one page render request,
 * named like a macro would name it (the production days come from the page's url; the
 * server needs some days, so a page without any is rendered "for" today). hrefFor is the
 * part of the kitchen url after # for the route.
 */
export function pageRenderRequest(route, hrefFor, {
  today = new Date().toISOString().slice(0, 10),
  pageCss = printPageCssEnabled(),
} = {}) {
  const label = routeNameToWords(route.name) || route.path;
  const baseline = inferBaseline(route);
  const {path, query = {}} = route;
  return {
    macroName: label,
    orientation: 'portrait',
    pageCss,
    target: {dates: baseline ? baseline.dates : [today]},
    slack: null,
    steps: [{label, href: hrefFor({path, query}), filename: stepFilename({label, path, query}, baseline || {})}],
  };
}

/**
 * A step's page as one editable url, tokens included:
 * /component-usage?dates={{dates}}&production={{production}}&diets=1&diets=2
 */
export function stepUrl(step) {
  const parts = [];
  Object.entries(step.query || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) {
      return;
    }
    [].concat(value).forEach(v => parts.push(`${encodeQueryPart(key)}=${encodeQueryPart(v)}`));
  });
  return parts.length > 0 ? `${step.path}?${parts.join('&')}` : step.path;
}

/**
 * The reverse of stepUrl: an edited url back to {path, query}. Repeated keys become
 * arrays. Throws when the url does not start with a path.
 */
export function parseStepUrl(text) {
  const trimmed = `${text || ''}`.trim().replace(/^#/, '');
  if (!trimmed.startsWith('/')) {
    throw new Error('the url must start with / (the part of the address after #)');
  }
  const [path, ...rest] = trimmed.split('?');
  const query = {};
  rest.join('?').split('&').filter(Boolean).forEach((pair) => {
    const [rawKey, ...rawValue] = pair.split('=');
    const key = decodeURIComponent(rawKey);
    const value = decodeURIComponent(rawValue.join('='));
    if (key in query) {
      query[key] = [].concat(query[key], value);
    } else {
      query[key] = value;
    }
  });
  return {path, query};
}

/** warnings for a hand edited url: literal dates replay unchanged, {{production}} is no longer filled in */
export function urlWarnings(text) {
  const url = `${text || ''}`;
  const dates = [...new Set(url.match(DATE_PATTERN) || [])];
  const warnings = dates.map(date => `date ${date} will replay unchanged - use {{date:0}}, {{date:1}}... or {{dates}} instead`);
  if (url.includes(PRODUCTION_TOKEN)) {
    warnings.push(`${PRODUCTION_TOKEN} is no longer filled in - write the production or shipping group (local, ship, CC) instead`);
  }
  return warnings;
}
